import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ChatSession } from "@/types/chat";

const PERSONA_BADGES: Record<string, string> = {
  reading_coach: "Coach",
  culture_guide: "Culture",
};

interface SessionHistorySidebarProps {
  sessions: ChatSession[];
  activeSessionId?: number;
  onSelectSession: (sessionId: number) => void;
  onNewChat: () => void;
  onDeleteSession?: (sessionId: number) => void;
  isLoading?: boolean;
}

function groupByDate(sessions: ChatSession[]) {
  const groups: { label: string; sessions: ChatSession[] }[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const todaySessions: ChatSession[] = [];
  const yesterdaySessions: ChatSession[] = [];
  const thisWeekSessions: ChatSession[] = [];
  const olderSessions: ChatSession[] = [];

  for (const s of sessions) {
    const d = new Date(s.updated_at);
    d.setHours(0, 0, 0, 0);
    if (d >= today) todaySessions.push(s);
    else if (d >= yesterday) yesterdaySessions.push(s);
    else if (d >= weekAgo) thisWeekSessions.push(s);
    else olderSessions.push(s);
  }

  if (todaySessions.length) groups.push({ label: "Today", sessions: todaySessions });
  if (yesterdaySessions.length) groups.push({ label: "Yesterday", sessions: yesterdaySessions });
  if (thisWeekSessions.length) groups.push({ label: "This Week", sessions: thisWeekSessions });
  if (olderSessions.length) groups.push({ label: "Older", sessions: olderSessions });

  return groups;
}

export function SessionHistorySidebar({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  isLoading,
}: SessionHistorySidebarProps) {
  const groups = groupByDate(sessions);

  return (
    <div className="w-64 border-l bg-muted/30 flex flex-col h-full">
      <div className="p-3 border-b">
        <Button
          onClick={onNewChat}
          variant="outline"
          className="w-full justify-start gap-2"
          size="sm"
        >
          <Plus className="w-4 h-4" />
          New Chat
        </Button>
      </div>

      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground text-center">
            Loading sessions...
          </div>
        ) : groups.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground text-center">
            No previous chats
          </div>
        ) : (
          <div className="py-2">
            {groups.map((group) => (
              <div key={group.label} className="mb-3">
                <h4 className="px-3 mb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {group.label}
                </h4>
                {group.sessions.map((session) => (
                  <div
                    key={session.id}
                    className={cn(
                      "group flex items-center gap-2 px-3 py-2 mx-1 rounded-md cursor-pointer text-sm transition-colors",
                      session.id === activeSessionId
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted text-foreground"
                    )}
                    onClick={() => onSelectSession(session.id)}
                  >
                    <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                    <span className="truncate flex-1">
                      {session.title || "New chat"}
                    </span>
                    {session.persona && PERSONA_BADGES[session.persona] && (
                      <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 flex-shrink-0">
                        {PERSONA_BADGES[session.persona]}
                      </Badge>
                    )}
                    {onDeleteSession && (
                      <button
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-destructive/10 hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteSession(session.id);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
